import { Controller } from 'react-hook-form';
import { useQueries } from 'react-query';
import { getAssetsShort } from 'services/assets-service';
import { getSystemsShort } from 'services/systems-service';
import { getAreasShort } from 'services/areas-service';
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'Components/ui';
import {
  FormWrap, FormCard, FormCardHeader, FormCardIcon, FormCardHeaderText,
  FormCardTitle, FormCardSubtitle,
  FormFieldGrid, FormFieldFull, FormLabel,
} from '../../styles';

const LinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
  </svg>
);

const isSameRecord = (option, val) => option._id === val._id;

export const Vinculos = ({ handleComplete, control, handleSubmit }) => {
  const { t } = useTranslation('mapping');
  const selectedTenant = useSelector(useAuthTenant);

  const onSubmit = () => handleComplete();

  const queries = useQueries([
    {
      queryKey: ['getAreas'],
      queryFn: async () => {
        if (!selectedTenant.slug) return [];
        const result = await getAreasShort(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
    {
      queryKey: ['getAssets'],
      queryFn: async () => {
        if (!selectedTenant.slug) return [];
        const result = await getAssetsShort(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
    {
      queryKey: ['getSystems'],
      queryFn: async () => {
        if (!selectedTenant.slug) return [];
        const result = await getSystemsShort(selectedTenant.slug);
        return result?.data.length > 0 ? result?.data : [];
      },
    },
  ]);

  const [
    { data: areas },
    { data: assets },
    { data: systems },
  ] = queries;

  const visibleAreas = (areas ?? []).filter(a => !a.archived);
  const visibleAssets = (assets ?? []).filter(a => !a.archived);
  const visibleSystems = (systems ?? []).filter(s => !s.archived);

  return (
    <form id="proc-form" onSubmit={handleSubmit(onSubmit)}>
      <FormWrap>
        <FormCard>
          <FormCardHeader>
            <FormCardIcon $gradient="linear-gradient(135deg,var(--vo-cobalto),var(--vo-capri))">
              <LinkIcon />
            </FormCardIcon>
            <FormCardHeaderText>
              <FormCardTitle>{t('processes.form.card.bonds')}</FormCardTitle>
              <FormCardSubtitle>{t('processes.form.card.bondsSub')}</FormCardSubtitle>
            </FormCardHeaderText>
          </FormCardHeader>

          <FormFieldGrid>
            <Controller
              control={control}
              name="areas"
              render={({ field: { onChange, value } }) => (
                <FormFieldFull>
                  <FormLabel>{t('processes.form.fields.linkedAreas')}</FormLabel>
                  <Autocomplete
                    multiple
                    filterSelectedOptions
                    isOptionEqualToValue={isSameRecord}
                    options={visibleAreas}
                    getOptionLabel={(item) => item.name}
                    value={value || []}
                    onChange={onChange}
                    placeholder={t('processes.form.placeholders.searchArea')}
                  />
                </FormFieldFull>
              )}
            />

            <Controller
              control={control}
              name="systems"
              render={({ field: { onChange, value } }) => (
                <FormFieldFull>
                  <FormLabel>{t('processes.form.fields.linkedSystems')}</FormLabel>
                  <Autocomplete
                    multiple
                    filterSelectedOptions
                    isOptionEqualToValue={isSameRecord}
                    options={visibleSystems}
                    getOptionLabel={(item) => item.name}
                    value={value || []}
                    onChange={onChange}
                    placeholder={t('processes.form.placeholders.searchSystem')}
                  />
                </FormFieldFull>
              )}
            />

            <Controller
              control={control}
              name="assets"
              render={({ field: { onChange, value } }) => (
                <FormFieldFull>
                  <FormLabel>{t('processes.form.fields.linkedAssets')}</FormLabel>
                  <Autocomplete
                    multiple
                    filterSelectedOptions
                    isOptionEqualToValue={isSameRecord}
                    options={visibleAssets}
                    getOptionLabel={(item) => item.name}
                    value={value || []}
                    onChange={onChange}
                    placeholder={t('processes.form.placeholders.searchAsset')}
                  />
                </FormFieldFull>
              )}
            />
          </FormFieldGrid>
        </FormCard>
      </FormWrap>

    </form>
  );
};
